import React from 'react';
import { Clock, HardHat as HardHatIcon } from 'lucide-react';
import { MoldOrder } from '../../types/mold';
import { MoldOrderFirstLevelType } from '../../types/mold';
import MoldOrderStatus from '../shared/MoldOrderStatus';
import { isFinalStep } from '../MoldDetail/utils';
import TimeStat from './TimeStat';
import Countdown from './Countdown';
import { useAppState } from '../../context/AppStateContext';
import { getIconComponent } from '../../utils/icon';

// Helper to get main type for header styling
const getMainType = (type: string): MoldOrderFirstLevelType | null => {
  if (type.startsWith('maintenance')) return 'maintenance';
  if (type.startsWith('repair')) return 'repair';
  if (type.startsWith('mold_acceptance')) return 'mold_acceptance';
  return null;
};

// Helper function to format duration since creation
const formatDurationSinceCreation = (createdAt: string): string => {
  const now = new Date();
  const createdDate = new Date(createdAt);
  const diffTime = Math.abs(now.getTime() - createdDate.getTime());

  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days} วัน ${hours} ชั่วโมง`;
  } else if (hours > 0) {
    return `${hours} ชั่วโมง`;
  } else {
    const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    return `${minutes} นาที`;
  }
};

interface MoldOrderHeaderProps {
  moldOrder: MoldOrder;
}

export const MoldOrderHeader: React.FC<MoldOrderHeaderProps> = ({ moldOrder }) => {
  const { getMoldOrderTypeLabel, getMoldOrderCardHeadlines, getHeaderStyleConfig } = useAppState();
  const mainType = getMainType(moldOrder.type);
  const headerStyle = getHeaderStyleConfig(mainType);
  const barColorClass = headerStyle.headerBgColor || 'bg-gray-300';
  const isInFinalState = isFinalStep(moldOrder.status);

  // Get icon from uiConfig
  const IconComponent = headerStyle.uiConfig 
    ? getIconComponent(headerStyle.uiConfig) ?? HardHatIcon 
    : HardHatIcon;

  const headlines = getMoldOrderCardHeadlines(moldOrder);

  return (
    <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start sm:gap-4">
      <div className="w-full flex items-center gap-4 sm:gap-5">
        <div className={`hidden sm:block h-20 w-1.5 rounded-full ${barColorClass}`}></div>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <div>
            <h1 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2 flex items-center gap-2 sm:gap-3 truncate">
              {`ใบงาน${getMoldOrderTypeLabel(moldOrder.type)} `}
              {headlines.title}
            </h1>
            <div className="flex flex-wrap gap-2 items-center">
              {headlines.subtitle && (
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {headlines.subtitle}
                </span>
              )}
              <span className="text-sm text-gray-500 py-1">
                เลขที่ใบงาน {moldOrder.id}
              </span>
              <MoldOrderStatus moldOrder={moldOrder} />
            </div>
          </div>
          {/* Transparent icon next to info block */}
          <span className="hidden sm:flex items-center justify-center ml-2 opacity-10" style={{ minWidth: '4.5rem' }}>
            <IconComponent className={`w-16 h-16 ${barColorClass.replace('bg-', 'text-')}`} />
          </span>
        </div>
      </div>
      {moldOrder.orderDetail.dueDate ? (
        <div className="w-full sm:w-auto shrink-0 mt-3 sm:mt-0 flex justify-between sm:justify-end w-full sm:w-auto">
          {isInFinalState ?
            <TimeStat targetDate={moldOrder.orderDetail.dueDate} completedDate={moldOrder.updatedAt} /> :
            <Countdown targetDate={moldOrder.orderDetail.dueDate} />
          }
        </div>
      ) : isInFinalState ? (
        <div className="w-full sm:w-auto shrink-0 mt-3 sm:mt-0 flex justify-between sm:justify-end w-full sm:w-auto">
          <TimeStat targetDate={null} completedDate={moldOrder.updatedAt} createdAt={moldOrder.createdAt} />
        </div>
      ) : (
        <div className="w-full sm:w-auto shrink-0 mt-3 sm:mt-0 flex justify-between sm:justify-end w-full sm:w-auto">
          <div className="flex flex-col gap-1 items-center sm:items-end">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" />
              <span>อายุใบงานในระบบ:</span>
            </div>
            <div className="text-lg font-semibold text-gray-800">
              {formatDurationSinceCreation(moldOrder.createdAt)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoldOrderHeader;
